import { cn } from '@/lib/utils';
import { Button } from '@startupquickstart/frontend';
import { Sparkles, Timer } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { buildDeck, type MemoryCard } from './minigame-logic';
import { parseMemoryMatch } from './types';
import { DemoNotConfigured } from './DemoChrome';
import type { DemoChallenge } from './types';

type GameState = 'idle' | 'playing' | 'won' | 'lost';

const FLIP_BACK_DELAY_MS = 800;

/** Web replica of the mobile memory-match minigame (preview only). */
export function MemoryMatchDemo({ challenge }: { challenge: DemoChallenge }) {
  const { cardImages, gridSize, timeLimit } = parseMemoryMatch(challenge.minigameConfig);
  const pairCount = Math.floor((gridSize * gridSize) / 2);

  const [deck, setDeck] = useState<MemoryCard[]>(() =>
    buildDeck(pairCount, cardImages.length || 1),
  );
  const [flipped, setFlipped] = useState<number[]>([]);
  const [matched, setMatched] = useState<Set<number>>(new Set());
  const [state, setState] = useState<GameState>('idle');
  const [secondsLeft, setSecondsLeft] = useState(timeLimit);
  const flipBackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setDeck(buildDeck(pairCount, cardImages.length || 1));
    setFlipped([]);
    setMatched(new Set());
    setState('idle');
    setSecondsLeft(timeLimit);
    if (flipBackTimer.current) clearTimeout(flipBackTimer.current);
  }, [challenge.id, pairCount, cardImages.length, timeLimit]);

  // Countdown ticker (only while playing + non-zero limit).
  useEffect(() => {
    if (state !== 'playing' || timeLimit === 0) return;
    if (secondsLeft <= 0) {
      setState('lost');
      return;
    }
    const id = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [state, secondsLeft, timeLimit]);

  // Win detection after each match update.
  useEffect(() => {
    if (state === 'playing' && matched.size === pairCount) setState('won');
  }, [matched, pairCount, state]);

  if (cardImages.length < 2) {
    return (
      <DemoNotConfigured>
        This memory game isn&apos;t fully configured yet — at least two card images are required.
      </DemoNotConfigured>
    );
  }

  const startGame = () => {
    setSecondsLeft(timeLimit);
    setState('playing');
  };

  const resetGame = () => {
    setDeck(buildDeck(pairCount, cardImages.length || 1));
    setFlipped([]);
    setMatched(new Set());
    setSecondsLeft(timeLimit);
    setState('playing');
  };

  const handleCardClick = (card: MemoryCard) => {
    if (state !== 'playing') return;
    if (matched.has(card.imageIndex) || flipped.includes(card.id) || flipped.length >= 2) return;

    const nextFlipped = [...flipped, card.id];
    setFlipped(nextFlipped);
    if (nextFlipped.length === 2) {
      const [a, b] = nextFlipped.map((id) => deck.find((c) => c.id === id));
      if (a && b && a.imageIndex === b.imageIndex) {
        setMatched((prev) => new Set(prev).add(a.imageIndex));
        setFlipped([]);
      } else {
        flipBackTimer.current = setTimeout(() => setFlipped([]), FLIP_BACK_DELAY_MS);
      }
    }
  };

  const isFaceUp = (card: MemoryCard) =>
    flipped.includes(card.id) || matched.has(card.imageIndex);
  const showCountdown = timeLimit > 0;
  const mmss = `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="font-mono text-xs font-medium uppercase tracking-[0.15em] text-muted-foreground">
          Memory match
        </span>
        {showCountdown && state === 'playing' && (
          <span
            className={cn(
              'inline-flex items-center gap-1 text-sm font-semibold',
              secondsLeft <= 10 ? 'text-red-600' : 'text-foreground',
            )}
          >
            <Timer className="h-3.5 w-3.5" /> {mmss}
          </span>
        )}
      </div>

      <div className="mx-auto w-fit rounded-2xl border bg-muted/30 p-3">
        <div
          className="grid gap-2"
          style={{ gridTemplateColumns: `repeat(${gridSize}, minmax(0, 1fr))` }}
        >
          {deck.map((card) => {
            const faceUp = isFaceUp(card);
            return (
              <button
                key={card.id}
                type="button"
                onClick={() => handleCardClick(card)}
                disabled={state !== 'playing' || faceUp}
                className={cn(
                  'flex h-16 w-16 items-center justify-center overflow-hidden rounded-xl border',
                  matched.has(card.imageIndex) ? 'border-emerald-400' : 'border-border',
                  faceUp ? 'bg-background' : 'bg-blue-900',
                )}
              >
                {faceUp ? (
                  <img
                    src={cardImages[card.imageIndex]}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <Sparkles className="h-5 w-5 text-white/70" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {state === 'won' && (
        <p className="rounded-xl bg-emerald-50 p-3 text-center text-sm font-semibold text-emerald-700">
          All pairs found!
        </p>
      )}
      {state === 'lost' && (
        <p className="rounded-xl bg-red-50 p-3 text-center text-sm font-medium text-red-700">
          Time&apos;s up. Reshuffle and try again.
        </p>
      )}

      {state === 'idle' && <Button onClick={startGame}>Start</Button>}
      {(state === 'lost' || state === 'won') && (
        <Button variant="outline" onClick={resetGame}>
          {state === 'won' ? 'Play again' : 'Reshuffle'}
        </Button>
      )}
    </div>
  );
}
